import type { KlaviyoSettingsUpdateBody } from '@flux/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type FormEvent, type JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { SaveButton } from '@/components/ui/save-button.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { testKlaviyoConnection, updateKlaviyoSettings, useKlaviyoSummary } from './api-klaviyo.js';
import { ConnectionTestButton, SecretField } from './recharge-parts.js';
import { SettingsSectionSkeleton } from './settings-parts.js';

export const KLAVIYO_SAVED_MESSAGE = 'Clé Klaviyo enregistrée';

/**
 * Onglet **Klaviyo** (`06` §11.4) : la connexion au compte Klaviyo, une
 * intégration à part entière. Les fonctions qui l'utilisent gardent leurs
 * propres réglages (mode de la synchronisation : Paramètres → Recharge).
 */
export function KlaviyoTab(): JSX.Element {
  const queryClient = useQueryClient();
  const query = useKlaviyoSummary();
  const [apiKey, setApiKey] = useState('');
  const save = useMutation({
    mutationFn: (body: KlaviyoSettingsUpdateBody) => updateKlaviyoSettings(body),
    onSuccess: (summary) => {
      queryClient.setQueryData(queryKeys.klaviyoSettings.summary(), summary);
      toast.success(KLAVIYO_SAVED_MESSAGE);
      setApiKey('');
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  if (query.isPending) {
    return <SettingsSectionSkeleton fields={1} />;
  }

  if (query.isError) {
    return (
      <PageSection title="Klaviyo">
        <ErrorState
          message={query.error instanceof ApiError ? query.error.message : 'Impossible de charger les réglages Klaviyo.'}
          detail={query.error instanceof ApiError ? query.error.detail : undefined}
          onRetry={() => {
            void query.refetch();
          }}
          className="border-0 bg-transparent py-6"
        />
      </PageSection>
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmed = apiKey.trim();
    if (trimmed) {
      save.mutate({ apiKey: trimmed });
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <PageSection
        title="Connexion"
        description="Clé API privée du compte Klaviyo, partagée par les fonctions de Flux qui écrivent sur les profils."
        footer={<SaveButton isDirty={apiKey.trim() !== ''} isLoading={save.isPending} />}
      >
        <div className="flex flex-col gap-4">
          <SecretField
            id="klaviyo-api-key"
            label="Clé API privée"
            configured={query.data.apiKeyConfigured}
            value={apiKey}
            onChange={setApiKey}
            hint={
              query.data.apiKeyConfigured
                ? 'Jamais réaffichée : laissez le champ vide pour conserver la clé enregistrée.'
                : 'Clé pk_… avec les droits profiles en lecture et écriture.'
            }
          />
          <ConnectionTestButton label="Tester la connexion" run={testKlaviyoConnection} />
          <p className="text-sm text-muted-foreground">
            Utilisée par la synchronisation de la catégorie d’abonné, réglée dans{' '}
            <Link
              to="/parametres"
              search={{ tab: 'recharge' }}
              className="rounded-sm font-medium text-foreground underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Paramètres → Recharge
            </Link>
            .
          </p>
        </div>
      </PageSection>
    </form>
  );
}
